import numeral from "numeral";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
pdfMake.vfs = pdfFonts.pdfMake.vfs;

import { exportCSVFile } from "../../../utilities/dataUtilities";
import { parse } from "json2csv";
import { agreeOptionGroups } from "./Constants";
import { getPDFHeader } from "./Common.Generators";
import { thresholdByYear } from "../../../constants/Survey";

export function generateReportData({ survey, datasets }) {
  let results = [];

  for (let i = 0; i < survey.categories.length; i++) {
    let category = JSON.parse(JSON.stringify(survey.categories[i])); // Ensure pointer ref is broken for safe mutation.
    let indexesToRemove = [];
    for (let ii = 0; ii < category.questions.length; ii++) {
      let question = category.questions[ii];

      if (getAgreeOptionGroup(question.options)) {
        if (question.children) {
          for (let iii = 0; iii < question.children.length; iii++) {
            let child = question.children[iii];
            child.results = getComparisonRows(child.key, question.options);
          }
        } else {
          question.results = getComparisonRows(question.key, question.options);
        }
      } else indexesToRemove.push(ii);
    }

    let result = {
      key: category.key,
      displayName: category.displayName,
      questions: category.questions.filter(
        (x, i) => !indexesToRemove.some((y) => i === y)
      ),
    };

    let indexes = [];
    result.questions.forEach((question) => {
      if (
        question.settings.find((x) => x.key === "private" && x.value === "true")
      ) {
        let index = result.questions.findIndex(
          (x) => x.questionNumber === question.questionNumber
        );
        if (index !== -1) {
          indexes.push(index);
        }
      }
    });

    indexes
      .sort((a, b) => b - a)
      .forEach((index) => {
        result.questions.splice(index, 1);
      });

    if (
      result.questions.some(
        (x) => x.type !== "textBlock" && x.type !== "checklist"
      )
    ) {
      if (result.questions.length > 0) results.push(result);
    }

    //if (result.questions.length > 0) results.push(result);
  }

  function getComparisonRows(key, options) {
    let questionDataset = getQuestionDatasets(
      key,
      options,
      JSON.parse(JSON.stringify(datasets))
    );
    let result = {};

    for (let datasetKey in datasets) {
      if (datasets[datasetKey]) {
        if (questionDataset[datasetKey])
          result[datasetKey] = getAgreeData(
            options,
            questionDataset[datasetKey],
            datasets?.state?.surveyId ?? survey.id
          );
        else
          result[datasetKey] = {
            count: null,
            total: null,
            percentage: null,
          };
      }
    }

    return result;
  }

  return results;
}

export function generateCSV({ survey, district, school, reportData }) {
  let data = [];

  let fields = [
    {
      label: "Category",
      value: "category",
    },
    {
      label: "Question",
      value: "question",
    },
    {
      label: "Sub Question",
      value: "subQuestion",
    },
  ];

  let datasetCountProp = (key) => `${key}_count`;
  let datasetTotalProp = (key) => `${key}_total`;
  let datasetPercentageProp = (key) => `${key}_percentage`;

  for (let i = 0; i < reportData.length; i++) {
    let category = reportData[i];
    for (let ii = 0; ii < category.questions.length; ii++) {
      let question = category.questions[ii];

      logFields(question.children ?? [question]);

      if (question.children) {
        for (let iii = 0; iii < question.children.length; iii++) {
          let child = question.children[iii];
          addRow(
            {
              category: category.displayName,
              question: `${question.questionNumber} ${question.text}`,
              subQuestion: `${question.questionNumber}.${child.questionNumber} ${child.text}`,
            },
            child.results
          );
        }
      } else
        addRow(
          {
            category: category.displayName,
            question: `${question.questionNumber} ${question.text}`,
            subQuestion: "",
          },
          question.results
        );
    }
  }

  try {
    const csv = parse(data, { fields });
    let year = new Date(survey.startDate).getFullYear();
    let fileName = `${year}_TeacherWorkingConditionSurvey_AgreeAnalysis`;

    if (district) {
      fileName += `_${district.id}`;
    }

    if (school) {
      fileName += `_${school.id}`;
    }

    let prependedText = `"${year} Teacher Working Condition Survey - Individual Item Analysis",\n`;
    prependedText += `"District: ${
      district ? district.name : "All Districts"
    }",\n`;
    prependedText += `"School: ${school ? school.name : "All Schools"}",\n\n`;

    exportCSVFile(prependedText + csv, fileName);
  } catch (err) {
    console.error(err);
  }

  function logFields(questions) {
    questions.forEach((x) => {
      for (let key in x.results) {
        if (!fields.some((x) => x.value === datasetPercentageProp(key))) {
          fields.push({
            label: `${key} - Agree Percent`,
            value: datasetPercentageProp(key),
          });
        }

        if (!fields.some((x) => x.value === datasetCountProp(key))) {
          fields.push({
            label: `${key} - Agree Count`,
            value: datasetCountProp(key),
          });
        }

        if (!fields.some((x) => x.value === datasetTotalProp(key))) {
          fields.push({
            label: `${key} - Response Total`,
            value: datasetTotalProp(key),
          });
        }
      }
    });
  }

  function addRow(root, results) {
    for (let key in results) {
      root[datasetCountProp(key)] = results[key]?.count;
      root[datasetTotalProp(key)] = results[key]?.total;
      root[datasetPercentageProp(key)] = results[key]
        ? numeral(results[key].percentage).format("0.00%")
        : null;
    }

    data.push(root);
  }
}

export async function generatePDF({ survey, district, school, reportData }) {
  let content = [];
  // Header
  content.push(
    await getPDFHeader(
      `${new Date(survey.startDate).getFullYear()} Agree Analysis`,
      district,
      school
    )
  );

  content.push({ text: " ", style: "header" });

  // Survey Data
  for (let i = 0; i < reportData.length; i++) {
    let category = reportData[i];
    let categoryContent = {
      text: category.displayName,
      style: "subheader",
      breakAfter: false,
    };

    for (let ii = 0; ii < category.questions.length; ii++) {
      let question = category.questions[ii];

      let stack = [];

      if (ii === 0) stack.push(categoryContent);

      stack.push({
        text: `${question.questionNumber} ${question.text}`,
        style: "question",
        breakAfter: false,
      });

      stack.push(genComparisonTableContent(question.children ?? [question]));
      content.push({ unbreakable: false, stack });
    }
  }

  pdfMake
    .createPdf({
      content,
      styles: {
        header: {
          fontSize: 18,
          bold: true,
          margin: [0, 0, 0, 10],
        },
        subTitle: {
          fontSize: 11,
        },
        subheader: {
          fontSize: 16,
          bold: true,
          margin: [0, 10, 0, 5],
        },
        question: {
          fontSize: 11,
          margin: [0, 5, 0, 2],
        },
        subQuestion: {
          fontSize: 10,
          margin: [10, 20, 2, 5],
        },
        comparisonTable: {
          fontSize: 10,
          margin: [0, 2, 10, 20],
          noBorders: true,
        },
        comparisonKey: {
          bold: true,
          alignment: "right",
          margin: [10, 5, 10, 5],
        },
        comparisonValue: {
          alignment: "right",
          margin: [10, 5, 10, 5],
        },
        comparisonOption: {
          margin: [5, 5, 20, 5],
        },
        countRowKey: {
          bold: true,
          margin: [5, 5, 20, 5],
        },
        countRowColumn: {
          alignment: "right",
          margin: [10, 5, 10, 5],
        },
        dividerCol: {
          margin: [0, 0.5, 0, 0],
          fillColor: "#666",
        },
      },
    })
    .open();

  function genComparisonTableContent(questions) {
    let header = [
      {
        text: "",
        style: "comparisonKey",
      },
    ];
    let widths = ["*"];
    let rows = [];

    // define header
    for (let i = 0; i < questions.length; i++) {
      for (let key in questions[i].results) {
        if (!header.some((x) => x.text == key)) {
          header.push({
            text: key,
            style: "comparisonKey",
          });

          widths.push(60);
        }
      }
    }

    // create rows
    for (let i = 0; i < questions.length; i++) {
      let question = questions[i];
      let rowColumns = [];
      rowColumns.push({
        text: `${question.questionNumber} ${question.text}`,
        style: "comparisonOption",
      });

      for (let ii = 1; ii < header.length; ii++) {
        let result = question.results[header[ii].text];
        rowColumns.push({
          text:
            result && result.percentage > -1
              ? numeral(result.percentage).format("0.00%")
              : "N/A",
          style: "comparisonValue",
        });
      }

      rows.push(rowColumns);
    }

    let body = [header].concat(rows);

    return {
      style: "comparisonTable",
      table: {
        widths,
        body,
      },
      layout: {
        hLineWidth(i) {
          if (i < 2) return 1;
          else return 1;
        },
        hLineColor(i) {
          if (i == 0) return "#333";
          if (i == 1) return "#999";
        },
        vLineWidth: () => 1,
        fillColor(rowIndex, node, columnIndex) {
          let blue = "#E8EFFB";
          let intersect = "#CEDAEF";

          var test = node.table.body[rowIndex][columnIndex].text;
          test = test.replace("%", "");

          if (!isNaN(test) && test.length > 0) {
            test = +test;
            if (test >= 70) return "#66BB6A";
            if (test >= 50) return "#82acff";
            if (test >= 30) return "#ffa726";
            return "#F44336";
          } else if (rowIndex % 2 !== 0) {
            if (columnIndex % 2 === 0) {
              return blue;
            } else {
              return intersect;
            }
          } else {
            return null;
          }
        },
      },
      unbreakable: false,
    };
  }
}

export function getQuestionDatasets(questionKey, options, datasets) {
  let result = {};

  for (let key in datasets) {
    if (datasets[key]) {
      let target = datasets[key].results.find((x) => x.key == questionKey);

      if (target) {
        result[key] = {
          counts: target.counts.filter((x) =>
            options.some((y) => y.value == x.key)
          ),
        };

        result[key].total = result[key].counts.reduce((prev, curr) => {
          return prev + curr.value;
        }, 0);
      }
    }
  }

  return result;
}

export function getKeyCount(obj, includeEmpty = true) {
  let count = 0;
  for (let key in obj) {
    if (includeEmpty == true || (obj[key] && includeEmpty == false)) count++;
  }

  return count;
}

export function getAgreeOptionGroup(options) {
  for (let i = 0; i < agreeOptionGroups.length; i++) {
    let group = agreeOptionGroups[i];
    let qualifies = true;

    options.forEach((x) => {
      if (
        !group.some(
          (y) =>
            y.text.toLowerCase() == x.text.toLowerCase() && y.value == x.value
        )
      )
        qualifies = false;
    });

    if (qualifies) return group;
  }

  return null;
}

export function getAgreeData(options, dataset, surveyId) {
   let minResponses = thresholdByYear.find((x) => x.surveyId === surveyId)
     .minResponses;


  let group = getAgreeOptionGroup(options);
  let agreeCount = 0;
  let sum = dataset.total;

  options.forEach((x) => {
    if (group.some((y) => y.isAgree && y.value == x.value)) {
      let count = dataset.counts.find((y) => y.key == x.value);
      if (count) agreeCount += count.value;
    } else if (group.some((y) => y.isDontKnow && y.value == x.value)) {
      //let count = dataset.counts.find(y => y.key == x.value);
      //if (count) sum += -count.value;
    }
  });

  let result = {
    count: agreeCount ?? 0,
    total: sum,
    percentage: !agreeCount ? 0 : agreeCount / sum,
  };

  return result.total >= minResponses ? result : null;
}
